import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Settings, ExitToApp } from '@mui/icons-material';
import { NavLink } from 'react-router-dom';
import { fontColorDarkTheme } from 'styles/global.styles';
import { getEmail, getUserName } from 'services/DefaultService';
import { useTranslation } from 'react-i18next';

const HeaderDashboard = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [langAnchor, setLangAnchor] = useState<null | HTMLElement>(null);
  const [user, setUser] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const { i18n } = useTranslation();

  const open = Boolean(anchorEl);
  const openLang = Boolean(langAnchor);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLangClick = (event: React.MouseEvent<HTMLElement>) => {
    setLangAnchor(event.currentTarget);
  };
  const handleLangClose = () => {
    setLangAnchor(null);
  };
  const handleChangeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    handleLangClose();
  };

  useEffect(() => {
    const userName = getUserName();
    if (typeof userName === 'string') {
      setUser(userName);
    } else if (Array.isArray(userName)) {
      setUser(userName.join(', '));
    } else {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    const email = getEmail();
    if (typeof email === 'string') {
      setEmail(email);
    } else if (Array.isArray(email)) {
      setEmail(email.join(', '));
    } else {
      setEmail(null);
    }
  }, []);

  return (
    <React.Fragment>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: '8vh',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 1.5,
          pr: 2,
        }}
      >
        <Tooltip title="Selecionar idioma">
          <IconButton onClick={handleLangClick}>
            <img
              src={
                i18n.language?.startsWith('pt')
                  ? 'https://upload.wikimedia.org/wikipedia/en/0/05/Flag_of_Brazil.svg'
                  : 'https://upload.wikimedia.org/wikipedia/en/9/9a/Flag_of_Spain.svg'
              }
              alt="Bandeira do idioma"
              style={{
                width: 32,
                height: 20,
                borderRadius: 2,
                border: '1px solid #555',
              }}
            />
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={langAnchor}
          open={openLang}
          onClose={handleLangClose}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          sx={{
            '& .MuiPaper-root': {
              borderRadius: 3,
              backgroundColor: '#222',
              color: '#fff',
            },
          }}
        >
          <MenuItem
            onClick={() => handleChangeLanguage('pt')}
            selected={i18n.language?.startsWith('pt')}
          >
            🇧🇷 Português
          </MenuItem>
          <MenuItem
            onClick={() => handleChangeLanguage('es')}
            selected={i18n.language?.startsWith('es')}
          >
            🇪🇸 Español
          </MenuItem>
        </Menu>

        <IconButton>
          <NotificationsIcon color="secondary" />
        </IconButton>
        <IconButton>
          <MoreVertIcon color="secondary" />
        </IconButton>
        <Tooltip title="Account settings">
          <IconButton
            onClick={handleClick}
            size="small"
            sx={{ ml: 2 }}
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
          >
            <Avatar sx={{ width: 32, height: 32 }}>
              {user ? user[0] : 'M'}
            </Avatar>
          </IconButton>
        </Tooltip>
      </Box>

      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              borderRadius: 4,
              minWidth: 350,
              minHeight: 250,
              padding: 2,
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '& .MuiListItemIcon-root': {
                color: '#fff',
              },
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
              backgroundColor: '#222',
              color: '#fff',
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem disabled>
          <Avatar sx={{ bgcolor: '#1976d2', width: 82, height: 82 }}>
            {user ? user[0] : 'M'}
          </Avatar>
          <Box>
            <Box sx={{ fontSize: 17, fontWeight: 'bold' }}>
              {user || 'Carregando...'}
            </Box>
            <Box sx={{ fontSize: 14, color: '#bbb' }}>{email}</Box>
          </Box>
        </MenuItem>
        <Divider sx={{ background: 'rgba(255,255,255,0.12)' }} />
        <NavLink to="/app/configurations" style={{ textDecoration: 'none' }}>
          <MenuItem
            onClick={handleClose}
            sx={{
              color: fontColorDarkTheme,
              width: '100%',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              textTransform: 'none',
            }}
          >
            <ListItemIcon>
              <Settings fontSize="small" sx={{ color: '#fff' }} />
            </ListItemIcon>
            <Typography sx={{ mr: 'auto', ml: -1, fontSize: 14 }}>
              Configurações
            </Typography>
          </MenuItem>
        </NavLink>
        <Divider sx={{ background: 'rgba(255,255,255,0.12)' }} />
        <NavLink to="/app/logout" style={{ textDecoration: 'none' }}>
          <Button
            variant="text"
            sx={{
              color: fontColorDarkTheme,
              width: '100%',
              p: 1.5,
              px: 2,
              display: 'flex',
              justifyContent: 'flex-start',
              textTransform: 'none',
            }}
            startIcon={<ExitToApp />}
          >
            <Typography sx={{ mr: 'auto', ml: 0, fontSize: 14 }}>
              Sair
            </Typography>
          </Button>
        </NavLink>
      </Menu>
    </React.Fragment>
  );
};

export default HeaderDashboard;
